import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";

const isImage = (file: File) => {
  const imageType = /image.*/;

  // don't try to process non-images
  return file.type.match(imageType);
};

class Uploader {
  upload: DirectUpload;

  thumb: HTMLDivElement;
  onComplete: (blob: ActiveStorage.Blob) => void;

  constructor(
    file: File,
    url: string,
    thumb: HTMLDivElement,
    onComplete: (blob: ActiveStorage.Blob) => void
  ) {
    this.upload = new DirectUpload(file, url, this);
    this.thumb = thumb;
    this.onComplete = onComplete;
  }

  uploadFile() {
    const progressBar = this.thumb.querySelector<HTMLDivElement>(
      ".upload-progress-bar"
    );
    if (progressBar?.style) progressBar.style.width = "0%";
    const uploadProgress =
      this.thumb.querySelector<HTMLDivElement>(".upload-progress");
    if (uploadProgress) uploadProgress.classList.remove("hidden");

    this.upload.create((error, blob) => {
      if (error) {
        // Handle the error
      } else {
        this.onComplete(blob);
      }
    });
  }

  directUploadWillStoreFileWithXHR(request: XMLHttpRequest) {
    request.upload.addEventListener("progress", (event) =>
      this.directUploadDidProgress(event)
    );
  }

  directUploadDidProgress(event: ProgressEvent<XMLHttpRequestEventTarget>) {
    const progress = (event.loaded * 100) / event.total;

    const progressBar = this.thumb.querySelector<HTMLDivElement>(
      ".upload-progress-bar"
    );

    if (progressBar?.style) progressBar.style.width = `${progress}%`;

    if (event.loaded == event.total) {
      const progress =
        this.thumb.querySelector<HTMLDivElement>(".upload-progress");
      if (progress) {
        progress.classList.add("hidden");
      }
    }
  }
}

export default class extends Controller<HTMLInputElement> {
  static targets = [
    "thumbs",
    "input",
    "thumb",
    "label",
    "thumbTemplate",
    "dropzone"
  ];

  declare readonly hasThumbTarget: boolean;
  declare readonly thumbTarget: HTMLDivElement;
  declare readonly thumbTargets: HTMLDivElement[];

  declare readonly hasThumbsTarget: boolean;
  declare readonly thumbsTarget: HTMLElement;

  declare readonly hasInputTarget: boolean;
  declare readonly inputTarget: HTMLInputElement;

  declare readonly hasLabelTarget: boolean;
  declare readonly labelTarget: HTMLElement;

  declare readonly hasThumbTemplateTarget: boolean;
  declare readonly thumbTemplateTarget: HTMLElement;

  declare readonly hasDropzoneTarget: boolean;
  declare readonly dropzoneTarget: HTMLElement;

  previewImage: HTMLImageElement;
  previewImageWrapper: HTMLElement;
  thumbTemplate: string | undefined;

  imageUploads = {};

  initialize() {
    this.uploadFile = this.uploadFile.bind(this);
    this.previewImageWrapper = document.createElement("div");
    this.previewImageWrapper.className =
      "flex grow items-center justify-center hidden";
    this.labelTarget.parentNode?.insertBefore(
      this.previewImageWrapper,
      this.labelTarget
    );
    this.previewImage = document.createElement("img");
    this.previewImage.style.maxHeight = "240px";

    this.toggleThumbsCarousel();

    this.previewImageWrapper.appendChild(this.previewImage);
  }

  toggleThumbsCarousel() {
    if (!this.hasThumbTarget) {
      this.thumbsTarget.classList.add("hidden");
      this.labelTarget.classList.remove("hidden");
      this.previewImageWrapper.classList.add("hidden");
    } else {
      this.showThumbImage(this.thumbTarget);
    }
  }

  thumbTemplateTargetConnected() {
    this.thumbTemplate = this.thumbTemplateTarget.dataset.template;
  }

  selectImage({ currentTarget: thumb }) {
    this.showThumbImage(thumb);
  }

  removeImage({ currentTarget: button }) {
    const thumb = button.closest("[data-inputs--dropzone-target=thumb]");
    thumb.remove();
    this.toggleThumbsCarousel();
  }

  showThumbImage(thumb: HTMLDivElement) {
    this.thumbsTarget.classList.remove("hidden");
    this.thumbTargets.forEach((aThumb) => {
      aThumb.classList.remove("border-primary");
    });
    thumb.classList.add("border-primary");
    this.labelTarget.classList.add("hidden");
    this.previewImageWrapper.classList.remove("hidden");
    if (thumb.dataset.previewUrl) {
      this.previewImage.src = thumb.dataset.previewUrl;
    }
  }

  handleDrop(event: DragEvent) {
    event.preventDefault();
    this.dropzoneTarget.classList.remove("dragging-over");
    const files = event.dataTransfer?.files ?? [];
    Array.from(files).filter(isImage).forEach(this.uploadFile);
  }

  handleDragEnter() {
    this.dropzoneTarget.classList.add("dragging-over");
  }

  handleDragLeave() {
    this.dropzoneTarget.classList.remove("dragging-over");
  }

  handleDragOver(e: DragEvent) {
    this.dropzoneTarget.classList.add("dragging-over");
    e.preventDefault();
  }

  // Bind to normal file selection
  handleChange() {
    Array.from(this.inputTarget.files ?? [])
      .filter(isImage)
      .forEach(this.uploadFile);
    // you might clear the selected files from the input
    this.element.value = "";
  }

  uploadFile(file: File) {
    // your form needs the file_field direct_upload: true, which
    //  provides data-direct-upload-url
    const url = this.inputTarget.dataset.directUploadUrl;

    let template = (this.thumbTemplate ?? "")
      .replace("{THUMB_PREVIEW_URL}", URL.createObjectURL(file))
      .replace("{THUMB_URL}", URL.createObjectURL(file))
      .replace("{THUMB_TARGET}", "thumb");

    if (!url) {
      return;
    }

    const tmp = document.createElement("div") as HTMLDivElement;
    tmp.innerHTML = template;
    const thumb = tmp.childNodes[0] as HTMLDivElement;

    this.thumbsTarget.insertBefore(thumb, this.thumbTemplateTarget);
    this.showThumbImage(thumb);

    const upload = new Uploader(file, url, thumb, (blob) => {
      template = template
        .replace("{THUMB_SIGNED_ID}", blob.signed_id)
        .replace(
          "{THUMB_ID}",
          `${blob.checksum}_${Math.floor(Math.random() * 100000) + 1}`
        );
      thumb.outerHTML = template;
    });

    upload.uploadFile();
    this.inputTarget.value = "";
  }
}
